import React, { useEffect, useRef, useState } from 'react'
import styles from 'styles/components/PaymentForm.module.sass'
import { Link, useNavigate } from 'react-router-dom';
import ZeroneLoader from 'components/common/ZeroneLoader';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg'
import AccountType from 'interface/AccountType';
import { useToast } from 'provider/ToastProvider';
import { createV2SetupIntent, activateV2Subscription, updateV2PaymentMethod, syncSubscriptionFromStripe, fetchUserSubscription } from 'api/SubscriptionApiService';
import PrimaryButton from 'components/common/PrimaryButton';
import { useAuth } from 'hooks/AuthContext';
import { logError } from 'services/logService/errorLogger';
import stripeErrorMessages from 'constants/data/StripeErrors';
import SubscriptionSetupForm from './SubscriptionSetupForm';
import { usePlanStatus } from 'provider/PlanStatusProvider';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')

const SubscriptionPaymentForm: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [accountType, setAccountType] = useState<AccountType | null>(null)
    const [setupIntentId, setSetupIntentId] = useState<string>('')
    const [setupIntentSecret, setSetupIntentSecret] = useState<string>('')
    const [message, setMessage] = useState<string>('')
    const [pollTimedOut, setPollTimedOut] = useState(false)
    const [initError, setInitError] = useState(false)
    const [retryCount, setRetryCount] = useState(0)
    const initInProgressRef = useRef(false)
    const { addToast } = useToast()
    const { user, userSubscription } = useAuth()
    const { activeAccountTypes: accountTypes, loading: planStatusLoading } = usePlanStatus()
    const navigate = useNavigate()

    const isUpdateFlow = userSubscription?.paymentStatus === 'overdue' || userSubscription?.paymentStatus === 'failed'

    useEffect(() => {
        if (planStatusLoading) return
        if (!user || !userSubscription) {
            addToast({ type: 'error', message: 'Please login to access this page' })
            navigate('/login')
            return
        }
        if (userSubscription.name === 'free') {
            addToast({ type: 'error', message: 'Please upgrade your account to access this page' })
            navigate('/')
            return
        }
        if (!userSubscription.paymentRequired) {
            addToast({ type: 'error', message: 'Your account is already active' })
            navigate('/')
            return
        }

        const currentAccountType = accountTypes.find((type) => type.name === userSubscription.name)
        if (!currentAccountType) {
            addToast({ type: 'error', message: 'We encountered an issue. Please try again later' })
            navigate('/')
            return
        }
        setAccountType(currentAccountType)

        if (initInProgressRef.current) return
        initInProgressRef.current = true

        let cancelled = false

        const SETUP_FAIL_KEY = 'payment_setup_fail_count'
        const recordSetupFailure = () => {
            const current = parseInt(sessionStorage.getItem(SETUP_FAIL_KEY) ?? '0', 10)
            sessionStorage.setItem(SETUP_FAIL_KEY, String(current + 1))
            return current + 1
        }
        const clearSetupFailures = () => sessionStorage.removeItem(SETUP_FAIL_KEY)

        const handleSetupFailure = (failCount: number) => {
            if (failCount >= 3) {
                addToast({ type: 'error', message: 'We\'re having trouble setting up your payment. Please contact support if this continues.' })
                // TODO: POST /support/alert with { userId, context: 'payment_setup_failure', failCount }
            } else {
                addToast({ type: 'error', message: 'We couldn\'t load your payment details. Please try again in a moment.' })
            }
            navigate('/')
        }

        const initSetupIntent = async () => {
            setLoading(true)
            setInitError(false)
            setMessage('Checking subscription status...')
            try {
                const sync = await syncSubscriptionFromStripe({ tag: 'SubscriptionPaymentForm' })
                if (cancelled) return
                if (sync?.paymentStatus === 'paid') {
                    window.dispatchEvent(new Event('authChange'))
                    navigate('/success', {
                        state: {
                            kind: 'subscription-active',
                            title: 'Subscription Active',
                            message: 'Your subscription is already active.',
                            note: 'Thank you for choosing Zerone. We look forward to serving you.',
                            redirectTo: '/',
                            messageType: 'success'
                        }
                    })
                    return
                }
                setMessage('Loading payment details...')
                const setup = await createV2SetupIntent({
                    tag: 'SubscriptionPaymentForm',
                    purpose: isUpdateFlow ? 'update_payment_method' : 'subscribe'
                })
                if (cancelled) return
                if (!setup?.clientSecret || !setup?.setupIntentId) {
                    const failCount = recordSetupFailure()
                    logError('Setup intent returned no client secret', { setup }, 'SubscriptionPaymentForm')
                    handleSetupFailure(failCount)
                    return
                }
                clearSetupFailures()
                setSetupIntentId(setup.setupIntentId)
                setSetupIntentSecret(setup.clientSecret)
            } catch (error) {
                logError('Error initialising setup intent', { error }, 'SubscriptionPaymentForm')
                if (!cancelled) {
                    const failCount = recordSetupFailure()
                    handleSetupFailure(failCount)
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                    setMessage('')
                }
            }
        }

        void initSetupIntent()

        return () => { cancelled = true; initInProgressRef.current = false }
    }, [user, userSubscription, retryCount, planStatusLoading])

    const showToast = (type: 'error' | 'success' | 'warning' | 'info', toast: string) => {
        addToast({ message: toast, type })
    }

    const handleChangePlan = () => {
        addToast({ type: 'info', message: 'Redirecting to change plan page...' })
        navigate('/change-plan')
    }

    const handleCardConfirmed = async (_paymentMethodId: string) => {
        setLoading(true)
        setMessage(isUpdateFlow ? 'Updating payment method...' : 'Activating subscription...')
        let shouldPoll = false
        let isRetryPoll = false

        try {
            if (isUpdateFlow) {
                const result = await updateV2PaymentMethod({ tag: 'SubscriptionPaymentForm', setupIntentId })
                if (result.success && result.paymentSuccess) {
                    shouldPoll = true
                    setMessage('Confirming your payment...')
                } else if (result.success && !result.paymentSuccess) {
                    shouldPoll = true
                    isRetryPoll = true
                    setMessage('Your card was saved. Waiting for payment confirmation...')
                } else {
                    showToast('error', 'Something went wrong updating your payment method. Please try again.')
                }
            } else {
                const result = await activateV2Subscription({ tag: 'SubscriptionPaymentForm', setupIntentId })
                if (result.success) {
                    shouldPoll = true
                    setMessage('Confirming your payment...')
                } else {
                    showToast('error', 'Something went wrong activating your subscription. Please contact support if the issue persists.')
                }
            }
        } finally {
            if (!shouldPoll) {
                setLoading(false)
                setMessage('')
            }
        }

        if (shouldPoll) {
            const maxAttempts = isRetryPoll ? 20 : 8
            let attempts = 0
            const interval = setInterval(() => {
                attempts++
                void fetchUserSubscription().then((sub) => {
                    if (sub?.paymentStatus === 'paid') {
                        clearInterval(interval)
                        setLoading(false)
                        setMessage('')
                        window.dispatchEvent(new Event('authChange'))
                        navigate('/success', {
                            state: {
                                kind: 'payment-confirmed',
                                title: isUpdateFlow ? 'Payment Successful' : 'Subscription Activated',
                                message: isUpdateFlow ? 'Your payment was processed successfully.' : 'Your subscription has been activated.',
                                note: 'Thank you for choosing Zerone. We look forward to serving you.',
                                redirectTo: '/',
                                messageType: 'success'
                            }
                        })
                        return
                    }
                    if (attempts >= maxAttempts) {
                        clearInterval(interval)
                        setLoading(false)
                        setMessage('')
                        if (isRetryPoll) {
                            showToast('info', 'Your card has been saved. We\'ll email you once your payment is confirmed.')
                            navigate('/')
                        } else {
                            setPollTimedOut(true)
                        }
                    }
                }).catch(() => {
                    if (attempts >= maxAttempts) {
                        clearInterval(interval)
                        setLoading(false)
                        setMessage('')
                        if (isRetryPoll) {
                            showToast('info', 'Your card has been saved. We\'ll email you once your payment is confirmed.')
                            navigate('/')
                        } else {
                            setPollTimedOut(true)
                        }
                    }
                })
            }, 2500)
        }
    }

    const handlePaymentFailure = (errorMessage: string) => {
        if (typeof errorMessage !== 'string' || !errorMessage) {
            showToast('error', 'Something went wrong. Please try again.')
            return
        }
        const error = errorMessage.toLowerCase()
        const defaultError = 'Payment failed. Please contact support if the issue persists.'
        const errorKey = Object.keys(stripeErrorMessages).find((key) => error.includes(key))
        const errorMsg = errorKey ? stripeErrorMessages[errorKey] : undefined
        showToast('error', errorMsg ?? defaultError)
    }

    const handleCancelPayment = () => {
        showToast('warning', 'Payment cancelled. You can complete payment anytime from your profile.')
        navigate('/')
    }

    if (userSubscription?.name === 'free' && !userSubscription?.paymentRequired) {
        return (
            <div className={styles['payment-form']}>
                <h1>Welcome to Zerone</h1>
                <p>Your account is already active. Please login to access your account</p>
                <Link to='/login'>Login</Link>
            </div>
        )
    }

    /* eslint-disable @typescript-eslint/no-misused-promises */
    return (
        <div className={styles['main-payment-form']}>
            <div className={styles['header-container']}>
                <div className={styles['logo-container']}>
                    <Link to="/">
                        <img src={zeroneLogoDark} className={styles['zerone-logo']} alt="Zerone AnalytiQs Logo" />
                    </Link>
                </div>
                <h2 className={styles['form-title']}>{!loading ? (isUpdateFlow ? 'Update payment method' : 'Enter payment information') : ''}</h2>
                <p className={styles['form-subtitle']}>{!loading ? `We will automatically bill each ${userSubscription?.billingCycle === 'monthly' ? 'month' : 'year'}.` : ''}</p>
            </div>
            {loading ? (
                <div className={styles['loader']}>
                    <ZeroneLoader text={message} />
                </div>
            ) : initError ? (
                <div className={styles['message-section']}>
                    <p className={styles['message']}>We were unable to load your payment details. Please try again or contact support if the issue persists.</p>
                    <PrimaryButton
                        text='Try Again'
                        primaryColor='#FF5522'
                        textColor='#FFFFFF'
                        hoverColor='#FFFFFF'
                        hoverTextColor='#FF5522'
                        doSomething={() => {
                            setInitError(false)
                            setSetupIntentSecret('')
                            setRetryCount(c => c + 1)
                        }}
                    />
                </div>
            ) : pollTimedOut ? (
                <div className={styles['message-section']}>
                    <p className={styles['message']}>Your payment was received. It may take a moment to reflect on your account.</p>
                    <PrimaryButton
                        text='Continue to Dashboard'
                        primaryColor='#FF5522'
                        textColor='#FFFFFF'
                        hoverColor='#FFFFFF'
                        hoverTextColor='#FF5522'
                        doSomething={() => {
                            window.dispatchEvent(new Event('authChange'))
                            navigate('/')
                        }}
                    />
                </div>
            ) : setupIntentSecret ? (
                <div className={styles['main-wrapper']}>
                    <div className={styles['summary-section']}>
                        <div className={styles['top-summary']}>
                            <label className={styles['radio-label']}>
                                <input
                                    type="radio"
                                    name="accountType"
                                    value={userSubscription?.billingCycle}
                                    checked={true}
                                    className={styles['radio-input']}
                                />
                                <span className={styles['radio-text']} style={{ color: accountType?.color }}>
                                    {accountType?.name}
                                </span>
                            </label>
                            <div className={styles['right']}>
                                <span className={styles['subscription-type']}>{userSubscription?.billingCycle === 'monthly' ? 'Monthly billing' : 'Annual billing'}</span>
                            </div>
                        </div>
                        <p className={styles['account-description']}>{accountType?.description}</p>
                        <span className={styles['change-plan']} onClick={handleChangePlan}>Change plan</span>
                    </div>
                    <div className={styles['divider']} />
                    <div className={styles['payment-section']}>
                        <Elements stripe={stripePromise}>
                            <SubscriptionSetupForm
                                setupIntentSecret={setupIntentSecret}
                                onPaymentSuccess={handleCardConfirmed}
                                onPaymentError={handlePaymentFailure}
                                onPaymentCancel={handleCancelPayment}
                            />
                        </Elements>
                    </div>
                </div>
            ) : (
                <div className={styles['message-section']}>
                    <p className={styles['message']}>{message}</p>
                    {message.toLowerCase().includes('log in') && (
                        <PrimaryButton
                            text='Login'
                            primaryColor='#FF5522'
                            textColor='#FFFFFF'
                            hoverColor='#FFFFFF'
                            hoverTextColor='#FF5522'
                            doSomething={() => navigate('/login')}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default SubscriptionPaymentForm;

